<template>
    <div>
        <slot :loading="loading || !ready" />

        <template v-if="ready">
            <div class="mb-4 mb-lg-5">
                <Filters
                    :value="value"
                    :criterias="criterias"
                    @input="handleValueChanged"
                    :key="hydratingKey"
                />
            </div>

            <template v-if="jobs.length > 0">
                <div style="max-width: 700px">
                    <div class="list-group">
                        <template v-for="job in jobs">
                            <JobOfferItem :job="job" />
                        </template>
                    </div>
                </div>
            </template>
            <template v-else>
                <em class="text-muted">
                    Aucune offre n'a été trouvée avec ces critères
                </em>
            </template>


            <template v-if="results.meta.last_page > 1">
                <div class="mt-4">
                    <b-pagination
                        :value="currentPage"
                        :total-rows="results.meta.total"
                        :per-page="results.meta.per_page"
                        hide-goto-end-buttons
                        first-number
                        last-number
                        prev-class="mr-2"
                        next-class="ml-2"
                        @change="handlePageChanged"
                    >
                        <template v-slot:prev-text>
                            <svg width=".875em" height=".875em">
                                <use xlink:href="#sprite-arrow-left" />
                            </svg>
                        </template>
                        <template v-slot:next-text>
                            <svg width=".875em" height=".875em">
                                <use xlink:href="#sprite-arrow-right" />
                            </svg>
                        </template>
                    </b-pagination>
                </div>
            </template>

            <template v-if="loading">
                <div class="spinner-grow text-primary"></div>
            </template>
        </template>
    </div>
</template>

<script>
    import axios from 'axios';
    import isEqual from "lodash/isEqual";
    import router from './router';
    import { BPagination } from 'bootstrap-vue';
    import Filters from "./Filters";
    import JobOfferItem from "./JobOfferItem";
    import { serializeQuery, parseQuery } from "./util/query";
    import { getJobs } from "./api";

    export default {
        components: {
            Filters,
            JobOfferItem,
            BPagination,
        },
        props: {
            criterias: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                loading: false,

                value: null,
                results: null,
                error: null,
                hydratingKey: 0,
            }
        },
        computed: {
            jobs() {
                return this.results?.data ?? [];
            },
            currentPage() {
                return Number(this.$route.query.page ?? 1);
            }
        },
        methods: {
            handleValueChanged(value) {
                this.$router.push({
                    query: {
                        ...serializeQuery(value),
                        page: 1,
                    }
                });
            },
            handlePageChanged(page) {
                if(page === this.currentPage) {
                    return;
                }
                this.$router.push({
                    query: serializeQuery({
                        ...this.value,
                        page,
                    }),
                });
                window.scrollTo(0, this.$el.offsetTop - 180);
            },
            search(data) {
                this.error = null;
                const stalledTimeout = setTimeout(() => this.loading = true, 500);
                if(this.cancelSearch) {
                    this.cancelSearch();
                }
                return getJobs(data, {
                    cancelToken: new axios.CancelToken(c => this.cancelSearch = c),
                })
                .then(data => {
                    const { results, search } = data;
                    this.results = results;
                    this.value = search;
                    this.$emit('results');
                    this.loading = false;
                    clearTimeout(stalledTimeout)
                })
                .catch(error => {
                    if(axios.isCancel(error)) {
                        return;
                    }
                    this.loading = false;
                    clearTimeout(stalledTimeout);
                    this.error = 'Une erreur est survenue lors de la recherche';
                    return Promise.reject(error);
                });
            },
            init(route, oldRoute) {
                if(route && oldRoute && isEqual(route.query, oldRoute.query)) {
                    return;
                }
                const query = parseQuery(this.$route.query);
                const value = {
                    ...query,
                };
                return this.search(value);
            },
            setup() {
                return this.init()
                    .then(() => {
                        this.$router.replace({
                            name: 'home',
                            query: serializeQuery(this.value),
                        }).catch(() => {
                            // catch when route is same
                        });
                        this.$watch('$route', this.init);
                        this.$emit('ready');
                        this.ready = true;
                    })
                    .catch(error => {
                        // if error occurred with current query: redirect to /
                        if(location.search) {
                            this.$router.replace({ name:'home' });
                            this.setup();
                        }
                        return Promise.reject(error);
                    });
            },
        },
        async created() {
            await this.setup();

            if(this.currentPage > 1 && !this.results.length) {
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        page: 1,
                    },
                });
            }
        },
        mounted() {
            window.addEventListener('popstate', this.onPopState = () => {
                // force refresh when go to previous page (ios filters empty bug)
                this.hydratingKey++;
                // refresh after results received to sync current filters state
                this.$once('results', async () => {
                    await this.$nextTick();
                    this.hydratingKey++;
                });
            });
        },
        destroyed() {
            window.removeEventListener('popstate', this.onPopState);
        },
        router,
    }
</script>
